/**
 * BOUCHE : pas de disque, pas de rond. Deux levres a la couleur du client, un trait fin entre
 * elles, un reflet sur la levre basse. Elle respire a peine.
 *
 * L'IDEE, EN UN MOT : c'est le seul look qui PARLE. Au temps fort elle dit un mot de deux
 * syllabes, puis sourit ; elle sourit aussi a qui la survole. Rien n'est ecrit, rien n'est
 * illustre : le geste est la parole elle-meme.
 *
 * C'EST LE SEUL LOOK ANIME EN SMIL, ET C'EST UNE NECESSITE, PAS UN GOUT. Le CSS ne sait pas
 * interpoler la donnee d'un chemin : passer d'une bouche fermee a une bouche qui sourit demande
 * de deformer la courbe des levres, ce que seule une balise d'animation SVG fait. Le prix est
 * connu : le SMIL vit dans sa propre horloge, que « moins de mouvement » n'atteint pas en CSS. Il
 * se met en pause a la main, et Bouche le fait elle-meme au lieu de s'en remettre au modele.
 *
 * LES DEUX GESTES SONT DECLENCHES, PAS PROGRAMMES : chaque balise porte un debut indefini, et
 * c'est un appel qui la lance — le temps fort du modele, ou l'entree du pointeur. Une bouche qui
 * parlerait toute seule en boucle serait un tic, pas une adresse.
 *
 * A L'OUVERTURE elle se tait et s'estompe, et une croix se trace au meme trait que ses levres.
 *
 * CE FICHIER N'IMPORTE RIEN, PAR CONTRAT, et sa feuille est un gabarit de chaine : deux accents
 * graves dans le fichier, pas un de plus.
 */

/**
 * Lance les balises d'animation qui portent cette classe, sauf si le visiteur a demande moins de
 * mouvement — auquel cas l'horloge du SVG est deja en pause, et c'est elle qu'on interroge plutot
 * qu'un drapeau tenu a cote, qui pourrait mentir.
 */
const jouer = (conteneur, classe) => {
  const svg = conteneur.querySelector('svg');
  if (!svg) return;
  if (typeof svg.animationsPaused === 'function' && svg.animationsPaused())
    return;
  conteneur.querySelectorAll('.' + classe).forEach(balise => {
    if (typeof balise.beginElement === 'function') balise.beginElement();
  });
};

export default {
  id: 'bouche',

  css: `
/* LA BOUCHE FAIT 72 SUR 44 ET LE LANCEUR 64 SUR 64 : elle deborde de quatre pixels de chaque
   cote et se centre en hauteur sur le rond cliquable, qui ne bouge pas. Le lanceur garde ses
   dimensions pour que la zone de clic reste celle de la bulle d'aujourd'hui. */
.woot-lanceur[data-look="bouche"] .woot-bouche__figure {
  position: absolute;
  left: -4px;
  top: 10px;
  width: 72px;
  height: 44px;
}

.woot-lanceur[data-look="bouche"] .woot-bouche__figure svg {
  overflow: visible;
  filter: drop-shadow(0 8px 16px rgba(16, 19, 23, .22));
  filter: drop-shadow(0 8px 16px color-mix(in srgb, var(--client) 30%, transparent));
}

/* LES TEINTES SONT DANS LA FEUILLE, PAS DANS LES BALISES : un arret de degrade prend sa couleur
   en CSS comme n'importe quoi d'autre, et c'est le seul endroit ou la chaine de repli existe.
   Ecrites en attribut, les couleurs derivees n'auraient eu aucun repli. */
.woot-lanceur[data-look="bouche"] .woot-bouche__ton--ombre {
  stop-color: var(--client);
  stop-color: color-mix(in srgb, var(--client) 78%, black);
}

.woot-lanceur[data-look="bouche"] .woot-bouche__ton--plein { stop-color: var(--client); }

.woot-lanceur[data-look="bouche"] .woot-bouche__ton--clair {
  stop-color: var(--client);
  stop-color: color-mix(in srgb, var(--client) 72%, white);
}

.woot-lanceur[data-look="bouche"] .woot-bouche__levre-haute { fill: url(#woot-bouche-haute); }
.woot-lanceur[data-look="bouche"] .woot-bouche__levre-basse { fill: url(#woot-bouche-basse); }

.woot-lanceur[data-look="bouche"] .woot-bouche__interieur {
  fill: var(--client);
  fill: color-mix(in srgb, var(--client) 45%, black);
}

.woot-lanceur[data-look="bouche"] .woot-bouche__reflet { fill: rgba(255, 255, 255, .4); }

.woot-lanceur[data-look="bouche"] .woot-bouche__ligne {
  fill: none;
  stroke: var(--client);
  stroke: color-mix(in srgb, var(--client) 55%, black);
  stroke-width: 1.1;
  stroke-linecap: round;
}

/* LA RESPIRATION EST LA SEULE ANIMATION QUI TOURNE SEULE, et elle est a peine visible : deux et
   demi pour cent, cinq secondes. C'est ce qui distingue une bouche vivante d'un pictogramme. */
.woot-lanceur[data-look="bouche"] .woot-bouche__bouche {
  transform-box: fill-box;
  transform-origin: center;
  transition: opacity .4s ease, transform .4s ease;
}

.woot-lanceur[data-look="bouche"][data-etat="repos"] .woot-bouche__bouche {
  animation: woot-bouche-respire 5s ease-in-out infinite;
}

@keyframes woot-bouche-respire {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.025); }
}

/* LE TEMPS FORT EST LE MOT, ET LE MOT EST EN SMIL : le CSS ne fait ici que l'accompagner d'un
   rapprochement, parce qu'une bouche qui parle avance. L'echelle passe par la propriete scale et
   non par transform, deja occupee par la respiration : les deux se composent au lieu de se
   remplacer. */
.woot-lanceur[data-look="bouche"][data-etat="attention"] .woot-bouche__bouche {
  animation: woot-bouche-respire 5s ease-in-out infinite, woot-bouche-avance 2.2s ease-in-out;
}

@keyframes woot-bouche-avance {
  0%, 100% { scale: 1; }
  35%, 70% { scale: 1.07; }
}

/* AU SURVOL elle sourit — le sourire est en SMIL lui aussi, lance au pointeur ; la feuille se
   contente d'eclaircir les levres d'un cheveu pour que la reponse soit immediate meme avant que
   la courbe n'ait bouge. */
.woot-lanceur[data-look="bouche"]:hover .woot-bouche__figure svg {
  filter: brightness(1.06) drop-shadow(0 8px 16px rgba(16, 19, 23, .22));
  filter: brightness(1.06) drop-shadow(0 8px 16px color-mix(in srgb, var(--client) 30%, transparent));
}

/* OUVERT : elle se tait, s'estompe, et la croix se trace au meme trait — un trait apres l'autre.
   Les deux chemins sont le meme segment horizontal, tourne de part et d'autre : c'est la plume de
   la bouche qui ecrit la croix, pas deux barres posees dessus. */
.woot-lanceur[data-look="bouche"] .woot-bouche__croix path {
  fill: none;
  stroke: var(--woot-encre-auto, var(--client));
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-dasharray: 30;
  stroke-dashoffset: 30;
  transform-box: fill-box;
  transform-origin: center;
  transition: stroke-dashoffset .4s cubic-bezier(.4, 0, .2, 1);
}

.woot-lanceur[data-look="bouche"] .woot-bouche__croix--un { transform: rotate(45deg); }
.woot-lanceur[data-look="bouche"] .woot-bouche__croix--deux { transform: rotate(-45deg); }

.woot-lanceur[data-look="bouche"][data-etat="ouvert"] .woot-bouche__bouche {
  opacity: .28;
  animation: none;
  transform: scale(.96);
}

.woot-lanceur[data-look="bouche"][data-etat="ouvert"] .woot-bouche__croix--un {
  stroke-dashoffset: 0;
  transition-delay: .3s;
}

.woot-lanceur[data-look="bouche"][data-etat="ouvert"] .woot-bouche__croix--deux {
  stroke-dashoffset: 0;
  transition-delay: .6s;
}
`,

  monter: conteneur => {
    // Les deux formes de chaque levre : la bouche au repos, puis la meme qui sourit. Elles sont
    // ecrites ici plutot que dans la feuille parce qu'une balise d'animation interpole des
    // valeurs, et que ces valeurs sont des chemins.
    const hauteRepos =
      'M6 22 C 14 13, 24 11, 30 14.5 C 33 16, 34.5 16.5, 36 16.5 C 37.5 16.5, 39 16, 42 14.5 C 48 11, 58 13, 66 22 Z';
    const hauteSourire =
      'M5 19 C 14 12, 24 11, 30 14.5 C 33 16, 34.5 16.5, 36 16.5 C 37.5 16.5, 39 16, 42 14.5 C 48 11, 58 12, 67 19 Z';
    const basseRepos = 'M6 22 C 14 33, 24 37, 36 37 C 48 37, 58 33, 66 22 Z';
    const basseSourire = 'M5 19 C 14 32, 24 37, 36 37 C 48 37, 58 32, 67 19 Z';
    const ligneRepos = 'M9 22 C 20 23.5, 52 23.5, 63 22';
    const ligneSourire = 'M8 19.5 C 20 24.5, 52 24.5, 64 19.5';

    // L'INTERIEUR EPOUSE LA SILHOUETTE DES DEUX LEVRES, EN RETRAIT. Trace trop plat, il laissait
    // voir la page entre les levres quand elles s'ecartaient : un filet blanc en travers de la
    // bouche ouverte, vu sur la capture. Trace trop large, il depasserait au repos.
    const interieur =
      'M8 20 C 16 14, 26 12.5, 36 16.5 C 46 12.5, 56 14, 64 20 C 56 31, 47 35, 36 35 C 25 35, 16 31, 8 20 Z';

    // Une paire de balises par forme : l'aller au sourire et le retour au repos. Les deux figent
    // leur resultat, sinon la bouche reviendrait d'un coup a sa forme declaree a la fin du geste.
    const morphe = (repos, sourire) =>
      '<animate class="woot-bouche__sourit" attributeName="d" begin="indefinite" dur="0.4s" fill="freeze" calcMode="spline" keySplines="0.2 0.8 0.2 1" values="' +
      repos +
      ';' +
      sourire +
      '"/>' +
      '<animate class="woot-bouche__neutre" attributeName="d" begin="indefinite" dur="0.4s" fill="freeze" calcMode="spline" keySplines="0.2 0.8 0.2 1" values="' +
      sourire +
      ';' +
      repos +
      '"/>';

    // Le mot : deux syllabes, donc deux ouvertures inegales. La levre basse descend six puis cinq
    // pixels, la haute remonte a peine — c'est la mandibule qui bouge, pas le crane.
    const parle = valeurs =>
      '<animateTransform class="woot-bouche__parle" attributeName="transform" type="translate" begin="indefinite" dur="1.1s" values="' +
      valeurs +
      '" keyTimes="0;.25;.45;.7;1" calcMode="spline" keySplines="0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1;0.4 0 0.2 1"/>';

    const figure = document.createElement('div');
    figure.className = 'woot-bouche__figure';
    figure.innerHTML =
      '<svg viewBox="0 0 72 44" width="72" height="44" aria-hidden="true">' +
      '<defs>' +
      '<linearGradient id="woot-bouche-haute" x1="0" y1="0" x2="0" y2="1">' +
      '<stop class="woot-bouche__ton--ombre" offset="0"/>' +
      '<stop class="woot-bouche__ton--plein" offset="1"/>' +
      '</linearGradient>' +
      '<linearGradient id="woot-bouche-basse" x1="0" y1="0" x2="0" y2="1">' +
      '<stop class="woot-bouche__ton--plein" offset="0"/>' +
      '<stop class="woot-bouche__ton--clair" offset="1"/>' +
      '</linearGradient>' +
      '</defs>' +
      '<g class="woot-bouche__bouche">' +
      '<path class="woot-bouche__interieur" d="' +
      interieur +
      '"/>' +
      '<g>' +
      '<path class="woot-bouche__levre-basse" d="' +
      basseRepos +
      '">' +
      morphe(basseRepos, basseSourire) +
      '</path>' +
      '<ellipse class="woot-bouche__reflet" cx="36" cy="30.5" rx="9" ry="2.4"/>' +
      parle('0 0;0 6;0 1.5;0 5;0 0') +
      '</g>' +
      '<g>' +
      '<path class="woot-bouche__levre-haute" d="' +
      hauteRepos +
      '">' +
      morphe(hauteRepos, hauteSourire) +
      '</path>' +
      '<path class="woot-bouche__ligne" d="' +
      ligneRepos +
      '">' +
      morphe(ligneRepos, ligneSourire) +
      '</path>' +
      parle('0 0;0 -1.5;0 -0.3;0 -1.2;0 0') +
      '</g>' +
      '</g>' +
      '<g class="woot-bouche__croix">' +
      '<path class="woot-bouche__croix--un" d="M24 22 L 48 22"/>' +
      '<path class="woot-bouche__croix--deux" d="M24 22 L 48 22"/>' +
      '</g>' +
      '</svg>';

    conteneur.appendChild(figure);

    // LE SURVOL EST UN GESTE, PAS UN ETAT : elle sourit a qui approche, et redevient neutre quand
    // il s'eloigne — sauf si le temps fort est en cours, qui a sa propre fin.
    conteneur.addEventListener('mouseenter', () => {
      if (conteneur.dataset.etat !== 'ouvert')
        jouer(conteneur, 'woot-bouche__sourit');
    });
    conteneur.addEventListener('mouseleave', () => {
      if (conteneur.dataset.etat !== 'attention')
        jouer(conteneur, 'woot-bouche__neutre');
    });
  },

  gestes: {
    // LE MOT, PUIS LE SOURIRE. Le mot dure une seconde un dixieme ; le sourire vient juste apres,
    // et la bouche redevient neutre a la fin du temps fort — sauf si le pointeur est reste sur
    // elle entre-temps, auquel cas le sourire lui revient de droit.
    attention: conteneur => {
      jouer(conteneur, 'woot-bouche__parle');
      window.setTimeout(() => {
        if (conteneur.dataset.etat === 'attention')
          jouer(conteneur, 'woot-bouche__sourit');
      }, 1150);
      window.setTimeout(() => {
        if (
          typeof conteneur.matches === 'function' &&
          conteneur.matches(':hover')
        )
          return;
        jouer(conteneur, 'woot-bouche__neutre');
      }, 2300);
    },

    // Le panneau s'ouvre : elle se tait et reprend sa forme au repos, sous la croix qui se trace.
    ouvrir: conteneur => {
      jouer(conteneur, 'woot-bouche__neutre');
    },

    fermer: () => {},

    // LE SMIL N'EST PAS DU CSS : la regle « moins de mouvement » de la feuille ne l'atteint pas.
    // Le modele balaie deja les SVG animes, mais Bouche ne s'en remet pas a lui : un look qui
    // bouge doit savoir s'arreter seul, sinon il ne tient sa promesse que dans un seul montage.
    reduireLeMouvement: conteneur => {
      const svg = conteneur.querySelector('svg');
      if (svg && typeof svg.pauseAnimations === 'function')
        svg.pauseAnimations();
    },
  },
};
